import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

// WAV hening 1 sampel — dipakai untuk "membuka kunci" audio iOS pada sentuhan pertama bila playlist belum termuat
const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=";
const PREF_KEY = "jawsel_radio_on";
const MINI_KEY = "jawsel_radio_mini";

// Radio Jawsel di halaman kota: playlist aktif diputar berurutan & berulang; pengumuman yang sudah
// punya audio disisipkan tiap 2 lagu. Mulai otomatis pada sentuhan pertama (bila warga belum mematikan radio).
export function useRadio() {
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const idxRef = useRef(0);
  const errRef = useRef(0);
  const startedRef = useRef(false);
  const pendingRef = useRef(false);
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  const [on, setOn] = useState(() => localStorage.getItem(PREF_KEY) !== "0");
  const [playing, setPlaying] = useState(false);
  const [now, setNow] = useState(null);
  // Pil radio di peta kota dikecilkan jadi ikon bulat (pilihan diingat)
  const [mini, setMiniState] = useState(() => localStorage.getItem(MINI_KEY) === "1");
  const setMini = useCallback(v => { setMiniState(v); localStorage.setItem(MINI_KEY, v ? "1" : "0"); }, []);

  const audio = () => {
    if (!audioRef.current) { const a = new Audio(); a.preload = "auto"; audioRef.current = a; }
    return audioRef.current;
  };

  const playIndex = useCallback(i => {
    const q = queueRef.current;
    if (!q.length) return false;
    const k = ((i % q.length) + q.length) % q.length;
    idxRef.current = k;
    const a = audio();
    a.src = q[k].url;
    setNow(q[k]);
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    return true;
  }, []);

  // Muat playlist publik
  useEffect(() => {
    let alive = true;
    api.publicRadio().then(d => {
      if (!alive) return;
      const tracks = (d.tracks || []).map(t => ({ kind: "lagu", id: t.id, title: t.title, artist: t.artist || "", url: api.mediaUrl(t.url) }));
      const anns = (d.announcements || []).filter(a => a.audio_media_id).map(a => ({ kind: "pengumuman", id: a.id, title: a.title, artist: "Penyiar Jawsel", url: api.mediaUrl(a.audio_media_id) }));
      const q = []; let ai = 0;
      tracks.forEach((t, i) => { q.push(t); if (anns.length && i % 2 === 1) { q.push(anns[ai % anns.length]); ai++; } });
      queueRef.current = q;
      setReady(q.length > 0);
      setLoaded(true);
      if (pendingRef.current && q.length) { pendingRef.current = false; playIndex(0); }
    }).catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [playIndex]);

  // Event pemutar: lagu selesai → berikutnya; gagal → lewati (berhenti bila semua gagal)
  useEffect(() => {
    const a = audio();
    const onEnd = () => playIndex(idxRef.current + 1);
    const onErr = () => {
      errRef.current += 1;
      if (errRef.current < queueRef.current.length) playIndex(idxRef.current + 1); else setPlaying(false);
    };
    const onPlay = () => { setPlaying(true); errRef.current = 0; };
    const onPause = () => setPlaying(false);
    a.addEventListener("ended", onEnd); a.addEventListener("error", onErr); a.addEventListener("playing", onPlay); a.addEventListener("pause", onPause);
    return () => {
      a.removeEventListener("ended", onEnd); a.removeEventListener("error", onErr); a.removeEventListener("playing", onPlay); a.removeEventListener("pause", onPause);
      a.pause(); a.removeAttribute("src");
    };
  }, [playIndex]);

  const start = useCallback(() => {
    if (queueRef.current.length) return playIndex(idxRef.current);
    // playlist belum termuat: buka kunci audio dalam gestur ini, putar begitu data tiba
    const a = audio(); a.src = SILENT; a.play().catch(() => {});
    pendingRef.current = true;
    return false;
  }, [playIndex]);

  // Dipanggil pada sentuhan pertama di peta (dalam gestur pengguna → autoplay diizinkan)
  const onFirstTouch = useCallback(() => {
    if (startedRef.current || !on) return;
    startedRef.current = true;
    start();
  }, [on, start]);

  const toggle = useCallback(() => {
    startedRef.current = true;
    if (playing) {
      audio().pause(); setOn(false); localStorage.setItem(PREF_KEY, "0"); pendingRef.current = false;
      return;
    }
    setOn(true); localStorage.setItem(PREF_KEY, "1");
    if (now && queueRef.current.length) audio().play().then(() => setPlaying(true)).catch(() => setPlaying(false)); else start();
  }, [playing, now, start]);

  return { on, playing, now, ready, loaded, mini, setMini, count: queueRef.current.filter(x => x.kind === "lagu").length, toggle, onFirstTouch };
}
